import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import capitalizeFirst from '@zola-helpers/client/dist/es/transformers/capitalizeFirst';
import './ButtonV2.less';

/**
 * Simple button (For redesign)
 *
 * `import { ButtonV2 } from '@zola/zola-ui/src/components/Button';`
 *
 * @param {object} props - Button props
 * @param {string} props.component - Button component to be used ('button' | 'a' | 'div')
 * @param {object | string} props.children - Child elements
 * @param {((event) => void) | undefined} props.onClick - onClick event handler
 * @param {string} props.variant - Button variant method ('primary' | 'secondary')
 * @param {boolean=} props.bordered
 * @param {boolean=} props.compact
 * @param {boolean=} props.fullWidth
 * @param {boolean=} props.textTransform
 * @param {string=} props.className - class property
 * @param {('button' | 'submit' | 'reset')=} props.type
 * @param {('button' | 'link')=} props.role
 * @param {string=} props.target
 * @param {string=} props.rel
 * @param {boolean=} props.disabled
 * @param {(number | string)=} props.tabIndex
 * @param {string=} props.href
 * @param {RefObject<HTMLButtonElement>} props.innerRef
 * @deprecated components/ButtonV2 is deprecated, use components/ButtonV3
 */
const ButtonV2 = ({
  children,
  onClick,
  component: Component,
  variant,
  bordered,
  compact,
  fullWidth,
  textTransform,
  className,
  type,
  role,
  target,
  rel,
  disabled,
  tabIndex,
  href,
  innerRef,
  ...props
}) => {
  const classes = cx(
    'v2-button',
    { 'primary-button': variant === 'primary' },
    { 'primary-button--bordered': bordered },
    { 'secondary-button': variant === 'secondary' },
    { 'tertiary-button': variant === 'tertiary' },
    { 'transparent-button': variant === 'transparent' },
    { 'danger-button': variant === 'danger' },
    { 'button--compact': compact },
    { 'button--fullWidth': fullWidth },
    { 'button--disabled': disabled },
    className
  );

  const elementProps = {
    onClick: !disabled && onClick ? onClick : undefined,
    className: classes,
    href: Component === 'a' ? href : undefined,
    tabIndex: Component === 'button' ? tabIndex : undefined,
    ...props,
  };

  if (disabled) elementProps.disabled = disabled;
  if (Component === 'button') {
    elementProps.type = type;
    elementProps.role = role;
    if (tabIndex === undefined && disabled) {
      elementProps.tabIndex = -1;
    }
  } else if (Component === 'a') {
    if (target !== '') {
      elementProps.target = target;
      if (target === '_blank') {
        elementProps.rel = `noopener noreferrer ${rel}`;
      }
    }
    if (!href) {
      elementProps.href = '#';
    }
  }

  const applySentenceCase = textTransform && typeof children === 'string';

  return (
    <Component ref={innerRef} {...elementProps}>
      {applySentenceCase ? capitalizeFirst(String(children || ''), 1) : children}
    </Component>
  );
};

ButtonV2.defaultProps = {
  onClick: undefined,
  component: 'button',
  variant: 'primary',
  disabled: false,
  bordered: false,
  compact: false,
  fullWidth: false,
  textTransform: true,
  type: 'button',
  role: 'button',
  target: '',
  rel: '',
};

ButtonV2.propTypes = {
  component: PropTypes.oneOf(['button', 'a']),
  variant: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'transparent', 'danger']),
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  bordered: PropTypes.bool,
  compact: PropTypes.bool,
  fullWidth: PropTypes.bool,
  textTransform: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  role: PropTypes.oneOf(['button', 'link']),
  target: PropTypes.string,
  rel: PropTypes.string,
  tabIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  href: PropTypes.string,
  innerRef: PropTypes.shape({}),
};

export default ButtonV2;
